import React, { useEffect, useState, useCallback } from 'react';
import AppShell from '../../components/layout/AppShell';
import { ticketApi } from '../../services/api';
import api from '../../services/api';
import { fmtDT, TicketStatusBadge, PriorityBadge } from '../../utils/helpers';
import { Spinner, EmptyState, Modal } from '../../components/common';
import toast from 'react-hot-toast';
import { Search, MessageSquare, UserCheck, AlertTriangle } from 'lucide-react';

export function AgentTickets() {
  const [tickets, setTickets]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [filters, setFilters]   = useState({ status:'OPEN', priority:'', category:'', search:'' });
  const [selected, setSelected] = useState(null);
  const [detail, setDetail]     = useState(null);
  const [reply, setReply]       = useState('');
  const [sending, setSending]   = useState(false);
  const [stats, setStats]       = useState({ open:0, breached:0, urgent:0 });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await ticketApi.getAll({
        status:   filters.status   || undefined,
        priority: filters.priority || undefined,
        category: filters.category || undefined,
      });
      if (res.success) {
        setTickets(res.data);
        setStats({
          open:     res.data.filter(t => t.status === 'OPEN').length,
          breached: res.data.filter(t => t.sla_breached || (t.sla_deadline && new Date(t.sla_deadline) < new Date() && !['RESOLVED','CLOSED'].includes(t.status))).length,
          urgent:   res.data.filter(t => t.priority === 'URGENT').length,
        });
      }
    } finally { setLoading(false); }
  }, [filters.status, filters.priority, filters.category]);

  useEffect(() => { load(); }, [load]);

  const openTicket = async (ticket) => {
    setSelected(ticket);
    try {
      const res = await ticketApi.getOne(ticket.ticket_id);
      if (res.success) setDetail(res.data);
    } catch { toast.error('Failed to load ticket'); }
  };

  const sendReply = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      await ticketApi.addMessage(selected.ticket_id, { message: reply });
      setReply('');
      const res = await ticketApi.getOne(selected.ticket_id);
      if (res.success) setDetail(res.data);
    } catch { toast.error('Failed to send'); }
    finally { setSending(false); }
  };

  const updateStatus = async (status, resolution) => {
    try {
      await ticketApi.update(selected.ticket_id, { status, resolution: resolution || undefined });
      toast.success(`Ticket ${status.toLowerCase()}`);
      load();
      setSelected(null); setDetail(null);
    } catch { toast.error('Failed'); }
  };

  const assignToMe = async () => {
    try {
      await api.patch(`/tickets/${selected.ticket_id}`, { assignedAgent: 'self' });
      toast.success('Ticket assigned to you');
      const res = await ticketApi.getOne(selected.ticket_id);
      if (res.success) setDetail(res.data);
    } catch { toast.error('Failed'); }
  };

  const filtered = filters.search
    ? tickets.filter(t => t.ticket_no?.includes(filters.search.toUpperCase()) || t.customer_name?.toLowerCase().includes(filters.search.toLowerCase()) || t.subject?.toLowerCase().includes(filters.search.toLowerCase()))
    : tickets;

  const CATEGORIES = ['DELIVERY_ISSUE','DAMAGE_CLAIM','LOST_SHIPMENT','BILLING_QUERY','PICKUP_ISSUE','OTHER'];

  return (
    <AppShell title="CS — Support Tickets">

      {/* KPI strip — SLA-sorted (CP from CS module) */}
      <div className="stat-grid" style={{ gridTemplateColumns:'repeat(3,1fr)', marginBottom:20 }}>
        <div className="stat-card" style={{ cursor:'pointer' }} onClick={() => setFilters(f=>({...f,status:'OPEN'}))}>
          <div className="stat-label">Open Tickets</div>
          <div className="stat-value" style={{ color:'var(--danger)' }}>{stats.open}</div>
        </div>
        <div className="stat-card" style={{ cursor:'pointer', background: stats.breached>0 ? '#FEF2F2' : undefined }}>
          <div className="stat-label">SLA Breached</div>
          <div className="stat-value" style={{ color:'var(--danger)' }}>{stats.breached}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Urgent</div>
          <div className="stat-value" style={{ color:'var(--warning)' }}>{stats.urgent}</div>
        </div>
      </div>

      <div className="filters-bar">
        <div className="search-input">
          <Search size={14} aria-hidden="true" />
          <input aria-label="Search tickets" placeholder="Ticket no, customer, subject…" value={filters.search}
            onChange={e => setFilters(f=>({...f, search:e.target.value}))} />
        </div>
        <select className="form-control" style={{ width:150 }} aria-label="Filter by status" value={filters.status}
          onChange={e => setFilters(f=>({...f, status:e.target.value}))}>
          <option value="">All Statuses</option>
          {['OPEN','IN_PROGRESS','PENDING_CUSTOMER','RESOLVED','CLOSED'].map(s =>
            <option key={s} value={s}>{s.replace(/_/g,' ')}</option>
          )}
        </select>
        <select className="form-control" style={{ width:130 }} aria-label="Filter by priority" value={filters.priority}
          onChange={e => setFilters(f=>({...f, priority:e.target.value}))}>
          <option value="">All Priorities</option>
          {['URGENT','HIGH','MEDIUM','LOW'].map(p => <option key={p} value={p}>{p}</option>)}
        </select>
        <select className="form-control" style={{ width:160 }} aria-label="Filter by category" value={filters.category}
          onChange={e => setFilters(f=>({...f, category:e.target.value}))}>
          <option value="">All Categories</option>
          {CATEGORIES.map(c => <option key={c} value={c}>{c.replace(/_/g,' ')}</option>)}
        </select>
        <button className="btn btn-outline btn-sm" onClick={load}>↻</button>
      </div>

      <div className="card">
        <div className="table-wrap">
          {loading ? <Spinner /> : filtered.length ? (
            <table aria-label="Support tickets">
              <thead>
                <tr><th>Ticket No</th><th>Customer</th><th>Subject</th><th>Category</th><th>Priority</th><th>Agent</th><th>SLA Deadline</th><th>Status</th><th></th></tr>
              </thead>
              <tbody>
                {filtered.map(t => {
                  const breached = (t.sla_breached || (t.sla_deadline && new Date(t.sla_deadline) < new Date())) && !['RESOLVED','CLOSED'].includes(t.status);
                  return (
                    <tr key={t.ticket_id} style={{ background: breached ? '#FEF2F2' : undefined }}>
                      <td>
                        <code style={{ fontSize:'.78rem', color:'var(--navy)', fontWeight:700 }}>{t.ticket_no}</code>
                        {breached && <span title="SLA Breached"><AlertTriangle size={12} color="var(--danger)" style={{ marginLeft:4 }} /></span>}
                      </td>
                      <td>
                        <div style={{ fontSize:'.85rem', fontWeight:500 }}>{t.customer_name}</div>
                        <div className="text-sm text-muted">{t.customer_mobile}</div>
                      </td>
                      <td style={{ maxWidth:180 }}><span className="truncate" style={{ display:'block', fontSize:'.85rem' }}>{t.subject}</span></td>
                      <td><span className="badge badge-info" style={{ fontSize:'.68rem' }}>{t.category?.replace(/_/g,' ')}</span></td>
                      <td><PriorityBadge priority={t.priority} /></td>
                      <td className="text-sm">{t.agent_name || <span className="text-muted">Unassigned</span>}</td>
                      <td className="text-sm" style={{ color: breached ? 'var(--danger)' : undefined, fontWeight: breached ? 600 : 400 }}>
                        {t.sla_deadline ? fmtDT(t.sla_deadline) : '—'}
                      </td>
                      <td><TicketStatusBadge status={t.status} /></td>
                      <td>
                        <button className="btn btn-sm btn-outline" onClick={() => openTicket(t)}>
                          <MessageSquare size={12} /> View
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : <EmptyState icon="🎫" title="No tickets found" />}
        </div>
      </div>

      {/* Ticket detail modal */}
      <Modal open={!!selected} onClose={() => { setSelected(null); setDetail(null); setReply(''); }} title={`${selected?.ticket_no} — ${selected?.subject || ''}`} size="modal-lg">
        {detail ? (
          <div className="modal-body">
            {/* Info strip */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12, marginBottom:16, padding:12, background:'var(--gray-50)', borderRadius:8 }}>
              <InfoItem label="Customer"  v={`${detail.ticket.customer_name} · ${detail.ticket.customer_mobile}`} />
              <InfoItem label="Category"  v={detail.ticket.category?.replace(/_/g,' ')} />
              <InfoItem label="Tracking"  v={detail.ticket.tracking_id || '—'} />
              <InfoItem label="Priority"  v={<PriorityBadge priority={detail.ticket.priority} />} />
              <InfoItem label="Status"    v={<TicketStatusBadge status={detail.ticket.status} />} />
              <InfoItem label="SLA"       v={detail.ticket.sla_deadline ? fmtDT(detail.ticket.sla_deadline) : '—'} />
            </div>

            {/* Description */}
            {detail.ticket.description && (
              <div style={{ padding:'10px 12px', background:'var(--gray-50)', borderRadius:6, fontSize:'.85rem', marginBottom:12, borderLeft:'3px solid var(--navy)' }}>
                {detail.ticket.description}
              </div>
            )}

            {/* Thread */}
            <div style={{ maxHeight:300, overflowY:'auto', display:'flex', flexDirection:'column', gap:8, marginBottom:12 }} aria-live="polite">
              {detail.messages.map(m => (
                <div key={m.message_id} style={{
                  alignSelf: m.actor_type==='customer' ? 'flex-start' : 'flex-end',
                  maxWidth:'80%', padding:'8px 12px', borderRadius:12,
                  background: m.actor_type==='customer' ? '#EFF6FF' : m.actor_type==='bot' ? 'var(--gray-100)' : '#F0FDF4',
                  fontSize:'.85rem'
                }}>
                  <div style={{ fontSize:'.7rem', color:'var(--gray-400)', marginBottom:3 }}>
                    {m.actor_type==='customer' ? 'Customer' : m.actor_type==='agent' ? 'Agent' : 'Bot'} · {fmtDT(m.sent_at)}
                  </div>
                  {m.message_text}
                </div>
              ))}
              {!detail.messages.length && <div style={{ textAlign:'center', color:'var(--gray-400)', padding:16 }}>No messages yet</div>}
            </div>

            {/* Actions */}
            {!['RESOLVED','CLOSED'].includes(detail.ticket.status) && (
              <div>
                <textarea className="form-control" rows={3} aria-label="Reply to customer"
                  placeholder="Type reply to customer…" value={reply}
                  onChange={e => setReply(e.target.value)} style={{ marginBottom:8 }} />
                <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                  <button className="btn btn-primary" onClick={sendReply} disabled={sending || !reply.trim()}>
                    {sending ? 'Sending…' : 'Send Reply'}
                  </button>
                  {!detail.ticket.agent_name && (
                    <button className="btn btn-outline btn-sm" onClick={assignToMe}>
                      <UserCheck size={13} /> Assign to Me
                    </button>
                  )}
                  <button className="btn btn-success btn-sm" onClick={() => updateStatus('RESOLVED')}>✓ Resolve</button>
                  <button className="btn btn-outline btn-sm" onClick={() => updateStatus('IN_PROGRESS')}>In Progress</button>
                  <button className="btn btn-outline btn-sm" onClick={() => updateStatus('PENDING_CUSTOMER')}>Pending Customer</button>
                  <button className="btn btn-outline btn-sm" style={{ color:'var(--gray-400)' }} onClick={() => updateStatus('CLOSED')}>Close</button>
                </div>
              </div>
            )}
            {detail.ticket.resolution && (
              <div style={{ marginTop:12, padding:'8px 12px', background:'#F0FDF4', borderRadius:6, fontSize:'.82rem', borderLeft:'3px solid var(--success)' }}>
                <strong>Resolution:</strong> {detail.ticket.resolution}
              </div>
            )}
          </div>
        ) : <div className="loading-center" style={{ height:200 }}><div className="spinner" /></div>}
      </Modal>
    </AppShell>
  );
}

function InfoItem({ label, v }) {
  return (
    <div>
      <div style={{ fontSize:'.7rem', color:'var(--gray-400)', marginBottom:2 }}>{label}</div>
      <div style={{ fontSize:'.85rem', fontWeight:500 }}>{v}</div>
    </div>
  );
}
